import React, { useState } from "react"
import { X, Edit2, Save, Trash2, ChevronDown, ChevronRight, Copy } from "lucide-react"
import { Button, Input } from "@aix/ui"
import { trpc } from "../../../lib/trpc"
import toast from "react-hot-toast"

interface TableDrawerProps {
  tableId: string
  isOpen: boolean
  onClose: () => void
}

const TableDrawer: React.FC<TableDrawerProps> = ({ tableId, isOpen, onClose }) => {
  const [editingColumnId, setEditingColumnId] = useState<string | null>(null)
  const [editingDescription, setEditingDescription] = useState("")
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [expandedExampleId, setExpandedExampleId] = useState<string | null>(null)

  const utils = trpc.useUtils()

  // Fetch table details
  const { data: tableData, isLoading, refetch } = trpc.databrain.getTable.useQuery({ tableId })

  // Mutations
  const updateColumnDescription = trpc.databrain.updateColumnDescription.useMutation({
    onSuccess: () => {
      toast.success("Column description updated")
      setEditingColumnId(null)
      void refetch()
    },
    onError: (error) => {
      toast.error(`Failed to update: ${error.message}`)
    },
  })

  const deleteTable = trpc.databrain.deleteTable.useMutation({
    onSuccess: () => {
      toast.success("Table deleted successfully")
      // Invalidate graph query to refresh the view
      void utils.databrain.getGraph.invalidate()
      onClose()
    },
    onError: (error) => {
      toast.error(`Failed to delete table: ${error.message}`)
    },
  })

  const handleSaveDescription = (columnId: string) => {
    updateColumnDescription.mutate({
      columnId,
      description: editingDescription,
    })
  }

  const handleDeleteTable = () => {
    deleteTable.mutate({ tableId })
  }

  const handleCopyJSON = (jsonData: any) => {
    void navigator.clipboard.writeText(JSON.stringify(jsonData, null, 2))
    toast.success("JSON copied to clipboard")
  }

  const toggleExample = (exampleId: string) => {
    setExpandedExampleId(expandedExampleId === exampleId ? null : exampleId)
  }

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-background/80 z-40" onClick={onClose} />

      {/* Drawer */}
      <div className="fixed right-0 top-0 bottom-0 w-[600px] bg-background border-l border-border shadow-xl z-50 overflow-auto">
        {/* Header */}
        <div className="sticky top-0 bg-background border-b border-border px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-foreground">{tableData?.table.name || "Loading..."}</h2>
            <p className="text-sm text-muted-foreground">{tableData?.table.schema}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowDeleteConfirm(true)}
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {isLoading ? (
            <div className="text-muted-foreground">Loading table details...</div>
          ) : (
            <>
              {/* Table Info */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-foreground mb-2">Description</h3>
                <p className="text-sm text-muted-foreground">{tableData?.table.description || "No description"}</p>
              </div>

              {/* Columns */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-foreground mb-3">Columns ({tableData?.columns.length || 0})</h3>
                <div className="space-y-2">
                  {tableData?.columns.map((column) => (
                    <div key={column.id} className="bg-secondary border border-border rounded-lg p-3">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <div className="font-medium text-sm text-foreground">{column.name}</div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {column.dataType}
                            {column.isPrimaryKey && <span className="ml-2 text-primary">PK</span>}
                            {column.isForeignKey && <span className="ml-2 text-primary">FK</span>}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setEditingColumnId(column.id)
                            setEditingDescription(column.description || "")
                          }}
                        >
                          <Edit2 className="w-3 h-3" />
                        </Button>
                      </div>

                      {editingColumnId === column.id ? (
                        <div className="mt-2">
                          <Input
                            type="text"
                            placeholder="Add column description..."
                            value={editingDescription}
                            onChange={(e) => setEditingDescription(e.target.value)}
                            className="w-full mb-2"
                          />
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleSaveDescription(column.id)}
                              loading={updateColumnDescription.isLoading}
                            >
                              <Save className="w-3 h-3 mr-1" />
                              Save
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setEditingColumnId(null)}>
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground mt-2">{column.description || "No description"}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Relationships */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-foreground mb-3">
                  Relationships ({tableData?.relationships.length || 0})
                </h3>
                {tableData?.relationships.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No relationships defined</p>
                ) : (
                  <div className="space-y-2">
                    {tableData?.relationships.map((rel) => (
                      <div key={rel.id} className="bg-secondary border border-border rounded-lg p-3">
                        <div className="text-sm">
                          <span className="text-muted-foreground">{rel.type}</span>
                          {rel.isCanonical && (
                            <span className="ml-2 text-xs px-2 py-1 rounded bg-primary/10 text-primary">Canonical</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Examples */}
              <div>
                <h3 className="text-sm font-medium text-foreground mb-3">
                  Examples ({tableData?.examples.length || 0})
                </h3>
                {tableData?.examples.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No examples yet</p>
                ) : (
                  <div className="space-y-3">
                    {tableData?.examples.map((example) => {
                      const isExpanded = expandedExampleId === example.id
                      return (
                        <div key={example.id} className="bg-secondary border border-border rounded-lg overflow-hidden">
                          {/* Example Header - Clickable */}
                          <button
                            onClick={() => toggleExample(example.id)}
                            className="w-full p-3 text-left hover:bg-secondary/80 transition-colors flex items-start justify-between gap-2"
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <div className="font-medium text-sm text-foreground truncate">{example.name}</div>
                                {example.label && (
                                  <span className="text-xs px-2 py-0.5 rounded bg-primary/10 text-primary flex-shrink-0">
                                    {example.label}
                                  </span>
                                )}
                              </div>
                              {example.description && (
                                <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{example.description}</p>
                              )}
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleCopyJSON(example.jsonData)
                                }}
                                className="h-6 w-6 p-0"
                                title="Copy JSON"
                              >
                                <Copy className="w-3 h-3" />
                              </Button>
                              {isExpanded ? (
                                <ChevronDown className="w-4 h-4 text-muted-foreground" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-muted-foreground" />
                              )}
                            </div>
                          </button>

                          {/* Expanded JSON View */}
                          {isExpanded && (
                            <div className="border-t border-border">
                              <div className="p-3 bg-background/50">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-xs font-medium text-muted-foreground uppercase">JSON Data</span>
                                  <span className="text-xs text-muted-foreground">
                                    {Object.keys((example.jsonData as any) || {}).length} fields
                                  </span>
                                </div>
                                <pre className="text-xs bg-background border border-border rounded p-3 overflow-x-auto font-mono">
                                  <code className="text-foreground">{JSON.stringify(example.jsonData, null, 2)}</code>
                                </pre>
                              </div>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-background/95 z-[60] flex items-center justify-center p-4">
          <div className="bg-background border-2 border-destructive rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-5 h-5 text-destructive" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Delete Table?</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  Are you sure you want to delete{" "}
                  <span className="font-semibold text-foreground">{tableData?.table.name}</span>?
                </p>
                <p className="text-sm text-destructive font-medium">This will permanently delete:</p>
                <ul className="text-sm text-muted-foreground list-disc list-inside mt-1 space-y-1">
                  <li>{tableData?.columns.length || 0} columns</li>
                  <li>{tableData?.relationships.length || 0} relationships</li>
                  <li>{tableData?.examples.length || 0} examples</li>
                </ul>
                <p className="text-sm text-destructive font-medium mt-3">This action cannot be undone.</p>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setShowDeleteConfirm(false)} disabled={deleteTable.isLoading}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDeleteTable} loading={deleteTable.isLoading}>
                Delete Table
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default TableDrawer
